import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PortalHeading } from "@/pages/portal/PortalLayout";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const EMPTY = { company_name: "", contact_name: "", email: "", phone: "" };

const InsurerForm = ({ initial, onDone, editId }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/admin/insurance/insurers/${editId}`, { company_name: f.company_name, contact_name: f.contact_name, phone: f.phone });
        toast.success("Insurer updated");
      } else {
        const r = await api.post("/admin/insurance/insurers", f);
        toast.success(r.data.invite_sent ? "Insurer created, set-password email sent" : "Insurer created, but the email could not be sent. Use Resend invite.");
      }
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2 rounded-2xl bg-white p-5" data-testid={editId ? "insurer-edit-form" : "insurer-create-form"}>
      <Input required placeholder="Company name" value={f.company_name} onChange={set("company_name")} data-testid="insurer-field-company" />
      <Input required placeholder="Contact person" value={f.contact_name} onChange={set("contact_name")} data-testid="insurer-field-contact" />
      <Input required type="email" disabled={!!editId} placeholder="Email" value={f.email} onChange={set("email")} data-testid="insurer-field-email" />
      <Input required placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="insurer-field-phone" />
      <div className="sm:col-span-2 flex gap-3">
        <button disabled={busy} className="rounded-full bg-[#020165] px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="insurer-form-save">{editId ? "Save changes" : "Create & email invite"}</button>
        <button type="button" onClick={onDone} className="rounded-full border px-5 py-2 text-sm" data-testid="insurer-form-cancel">Cancel</button>
      </div>
    </form>
  );
};

export default function AdminInsurers() {
  const { isAdmin } = useAuth();
  const { data, reload } = useFetch("/admin/insurance/insurers");
  const [mode, setMode] = useState(null);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const done = () => { setMode(null); reload(); };
  return (
    <div className="space-y-4" data-testid="admin-insurers-page">
      <PortalHeading title="Insurers" sub="Insurance company accounts for the Insurer Portal"
        action={isAdmin && !mode && <button onClick={() => setMode("new")} className="rounded-full bg-[#F86900] px-5 py-2.5 text-sm font-bold text-[#0D141A]" data-testid="insurer-add-button">Add insurer</button>} />
      {mode === "new" && <InsurerForm onDone={done} />}
      <div className="space-y-2">
        {data?.insurers.map((i) => (mode === i.id ? <InsurerForm key={i.id} initial={i} editId={i.id} onDone={done} /> : (
          <div key={i.id} className="rounded-2xl bg-white p-4 flex flex-wrap items-center gap-4" data-testid={`insurer-row-${i.email}`}>
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-[#0D141A]">{i.company_name} <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${i.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`} data-testid={`insurer-status-${i.email}`}>{i.status}</span></p>
              <p className="text-sm text-[#56585E]">{i.contact_name} · {i.email} · {i.phone}</p>
              <p className="text-xs text-[#56585E]">{i.request_count} requests · {i.password_set ? `last login ${fmtDate(i.last_login_at)}` : "password not set yet"}</p>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-3 text-sm">
                <button onClick={() => setMode(i.id)} className="underline" data-testid={`insurer-edit-${i.email}`}>Edit</button>
                <button onClick={() => act(() => api.post(`/admin/insurance/insurers/${i.id}/invite`), "Invite email sent")} className="underline" data-testid={`insurer-invite-${i.email}`}>Resend invite</button>
                <button onClick={() => act(() => api.patch(`/admin/insurance/insurers/${i.id}`, { status: i.status === "active" ? "inactive" : "active" }), "Status updated")} className={`font-semibold ${i.status === "active" ? "text-red-600" : "text-emerald-700"}`} data-testid={`insurer-toggle-${i.email}`}>{i.status === "active" ? "Deactivate" : "Reactivate"}</button>
              </div>
            )}
          </div>
        )))}
        {data && !data.insurers.length && <p className="text-sm text-[#56585E]">No insurers yet.</p>}
      </div>
    </div>
  );
}
